/**
 * Eligible PA Statuses table (shared by Text Notes and Letter Upload), built on
 * the shared CustomTable. Renders PA status buckets with alias
 * (EDITABLE_TEXT_WITH_ICON) and an "include drug name" toggle. Text Notes shows
 * bucket as read-only text; Letter Upload also shows editable Document type +
 * Document name columns. "Add new status" lives in the table toolbar; each row
 * table toolbar; each row has an inline delete.
 */
import { useMemo } from "react";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { TableCellType } from "../../../../../components/custom-table/table";
import { PaStatusRow } from "./paStatusRows";

// Separator for encoding the row id inside the toggle id (uuids contain hyphens).
const TOGGLE_ID_PREFIX = "include-drug-name::";

interface EligiblePaStatusesTableProps {
  rows: PaStatusRow[];
  resetKey: number;
  /** Distinct CustomTable name so each tab keeps its own sort/search state. */
  tableName: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  /** When true, bucket is shown as plain text instead of inline-editable. */
  readOnlyBucket?: boolean;
  onBucketChange?: (id: string, value: string) => void;
  onAliasChange: (id: string, value: string) => void;
  onIncludeDrugNameChange: (id: string, value: boolean) => void;
  /** When true, render editable Document type + Document name columns (Letter Upload). */
  showDocumentColumns?: boolean;
  onDocumentTypeChange?: (id: string, value: string) => void;
  onDocumentNameChange?: (id: string, value: string) => void;
}

const editableTextHeader = (
  key: string,
  label: string,
  order: number,
  width: number,
) => ({
  key,
  label,
  order,
  width,
  sortable: true,
  subKey: "value",
  type: TableCellType.EDITABLE_TEXT_WITH_ICON,
});

const buildHeaders = (
  showDocumentColumns: boolean,
  readOnlyBucket: boolean,
) => {
  const headers = [
    readOnlyBucket
      ? {
          key: "bucket",
          label: "Bucket",
          order: 0,
          width: showDocumentColumns ? 18 : 35,
          sortable: true,
          type: TableCellType.STRING,
        }
      : editableTextHeader(
          "bucket",
          "Bucket",
          0,
          showDocumentColumns ? 18 : 35,
        ),
    editableTextHeader("alias", "Alias", 1, showDocumentColumns ? 16 : 35),
    {
      key: "includeDrugName",
      label: "Include Drug Name",
      order: 2,
      width: showDocumentColumns ? 16 : 22,
      sortable: false,
      type: TableCellType.TOGGLE_SWITCH,
    },
  ];
  if (showDocumentColumns) {
    headers.push(editableTextHeader("documentType", "Document type", 3, 18));
    headers.push(editableTextHeader("documentName", "Document name", 4, 24));
  }
  headers.push({
    key: "delete",
    label: "",
    order: 5,
    width: 8,
    sortable: false,
    type: TableCellType.DELETE_ICON,
  });
  return headers;
};

const EligiblePaStatusesTable = ({
  rows,
  resetKey,
  tableName,
  onAdd,
  onRemove,
  readOnlyBucket = false,
  onBucketChange,
  onAliasChange,
  onIncludeDrugNameChange,
  showDocumentColumns = false,
  onDocumentTypeChange,
  onDocumentNameChange,
}: EligiblePaStatusesTableProps) => {
  const tableHeaders = useMemo(
    () => buildHeaders(showDocumentColumns, readOnlyBucket),
    [showDocumentColumns, readOnlyBucket],
  );

  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        bucket: readOnlyBucket
          ? row.bucket || "—"
          : {
              value: row.bucket,
              placeholder: "Status bucket",
              emptyLabel: "—",
            },
        alias: { value: row.alias, placeholder: "Add alias", emptyLabel: "—" },
        includeDrugName: {
          id: `${TOGGLE_ID_PREFIX}${row.id}`,
          defaultChecked: row.includeDrugName,
        },
        documentType: {
          value: row.documentType ?? "",
          placeholder: "Document type",
          emptyLabel: "—",
        },
        documentName: {
          value: row.documentName ?? "",
          placeholder: "Document name",
          emptyLabel: "—",
        },
        delete: row.id,
      })),
    [rows, readOnlyBucket],
  );

  const handleCellChange = (
    rowData: { id?: string },
    fieldKey: string,
    newValue: string,
  ) => {
    if (!rowData?.id) return;
    if (fieldKey === "bucket") onBucketChange?.(rowData.id, newValue);
    else if (fieldKey === "alias") onAliasChange(rowData.id, newValue);
    else if (fieldKey === "documentType")
      onDocumentTypeChange?.(rowData.id, newValue);
    else if (fieldKey === "documentName")
      onDocumentNameChange?.(rowData.id, newValue);
  };

  const handleToggleChange = (data: { name: string; value: boolean }) => {
    const id = data.name.startsWith(TOGGLE_ID_PREFIX)
      ? data.name.slice(TOGGLE_ID_PREFIX.length)
      : "";
    if (id) onIncludeDrugNameChange(id, data.value);
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
      <div className="text-sm font-semibold text-primaryGray-1">
        Eligible PA Statuses
      </div>
      <CustomTable
        key={`${tableName}-${resetKey}`}
        tableName={tableName}
        tableHeaders={tableHeaders}
        tableData={tableData}
        count={tableData.length}
        totalCount={tableData.length}
        itemsPerPage={50}
        isFetching={false}
        hideSearchBar
        hidePagination
        showOtherButton
        otherButtonMeta={[
          {
            text: "Add new status",
            onClick: onAdd,
            disabled: false,
            buttonType: "primary",
            size: "medium",
          },
        ]}
        onCellValueChange={handleCellChange}
        onChangeEmit={handleToggleChange}
        onDeleteIconClick={(rowData: { id?: string }) =>
          rowData?.id && onRemove(rowData.id)
        }
      />
    </div>
  );
};

export default EligiblePaStatusesTable;
